import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, TextField, Paper, MenuItem } from "@material-ui/core";
import Logo from "../../assets/img/logo.png";
import { showToast, ToastType } from "../../utils/ToastUtil";
import { useToast } from "../../context/Toast/ToastProvider";
import {
  DeactivateRequest,
  DeactivateSendCode,
  DeactivateProcess,
  GetUnsubscribeReasons,
} from "../../services/deactivationService";
import { sessionGet, Logout } from "../../services/authService";

const DNI_REGEX = /^[0-9]{6,10}$/;
const CODE_REGEX = /^[0-9]{4,8}$/;

const STEP_DNI = "dni";
const STEP_CODE = "code";
const STEP_DONE = "done";

const PRIMARY = "#002c54";
const DANGER = "#da2037";

// El backend devuelve el email completo; lo enmascaramos por privacidad.
const maskEmail = (email) => {
  if (!email || !email.includes("@")) return email || "";
  const [local, domain] = email.split("@");
  const visible = local.slice(0, 1);
  return `${visible}${"*".repeat(Math.max(local.length - 1, 1))}@${domain}`;
};

// Mapea los códigos de error del backend a mensajes legibles.
const mapBackendError = (error, fallback) => {
  const message = error?.response?.data?.message;
  switch (message) {
    case "notFound":
      return "No encontramos un paciente con ese DNI.";
    case "isInactive":
      return "Este paciente ya se encuentra dado de baja.";
    case "expired":
      return "El código expiró (vence a las 24 hs). Solicitá uno nuevo.";
    case "incorrect":
      return "El código ingresado es incorrecto.";
    default:
      return fallback;
  }
};

const DarDeBaja = () => {
  const history = useHistory();
  const toastDispatch = useToast();

  const [step, setStep] = useState(STEP_DNI);
  const [dni, setDni] = useState("");
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [maskedMail, setMaskedMail] = useState("");
  const [reasons, setReasons] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingReasons, setLoadingReasons] = useState(false);

  // Cargamos los motivos al entrar al paso del código (donde vive el select),
  // no antes: así la llamada ocurre cuando el select va a mostrarse y no se
  // gasta si el usuario nunca pasa del DNI.
  useEffect(() => {
    if (step !== STEP_CODE || reasons.length > 0 || loadingReasons) return;
    let mounted = true;
    const loadReasons = async () => {
      setLoadingReasons(true);
      try {
        const data = await GetUnsubscribeReasons();
        if (mounted) setReasons(Array.isArray(data) ? data : []);
      } catch (e) {
        if (mounted) {
          showToast(
            toastDispatch,
            "No pudimos cargar los motivos de baja. Intentá nuevamente.",
            ToastType.ERROR,
            ToastType.ERROR,
          );
        }
      } finally {
        if (mounted) setLoadingReasons(false);
      }
    };
    loadReasons();
    return () => {
      mounted = false;
    };
    // useToast() devuelve una función nueva en cada render; solo dependemos de step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Vuelve a la pantalla anterior (ej. /mi-plan). Si se entró directo por URL
  // y no hay historial, cae al home.
  const handleBack = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push("/");
    }
  };

  // Paso 1: valida el paciente (request) y envía el código (send-code).
  const handleRequestCode = async (event) => {
    if (event) event.preventDefault();
    if (submitting) return;

    const cleanDni = dni.trim();
    if (!cleanDni) {
      setErrors({ dni: "Ingresá tu DNI" });
      return;
    }
    if (!DNI_REGEX.test(cleanDni)) {
      setErrors({ dni: "DNI inválido" });
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await DeactivateRequest(cleanDni);
      const sent = await DeactivateSendCode(cleanDni);
      setMaskedMail(maskEmail(sent?.mail));
      setStep(STEP_CODE);
      showToast(
        toastDispatch,
        "Te enviamos un código de confirmación por WhatsApp y email.",
        ToastType.SUCCESS,
        ToastType.SUCCESS,
      );
    } catch (e) {
      showToast(
        toastDispatch,
        mapBackendError(
          e,
          "No pudimos iniciar la baja. Por favor, intentalo más tarde.",
        ),
        ToastType.ERROR,
        ToastType.ERROR,
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Reenvía el código de confirmación (send-code) sin volver al paso 1.
  const handleResendCode = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const sent = await DeactivateSendCode(dni.trim());
      setMaskedMail(maskEmail(sent?.mail));
      showToast(
        toastDispatch,
        "Te reenviamos un nuevo código de confirmación.",
        ToastType.SUCCESS,
        ToastType.SUCCESS,
      );
    } catch (e) {
      showToast(
        toastDispatch,
        mapBackendError(
          e,
          "No pudimos reenviar el código. Por favor, intentalo más tarde.",
        ),
        ToastType.ERROR,
        ToastType.ERROR,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const validateProcess = () => {
    const next = {};
    if (!code.trim()) {
      next.code = "Ingresá el código que recibiste";
    } else if (!CODE_REGEX.test(code.trim())) {
      next.code = "Código inválido";
    }
    if (!reason) next.reason = "Seleccioná un motivo";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Paso 3: valida el código y ejecuta la baja (process).
  const handleProcess = async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (!validateProcess()) return;

    setSubmitting(true);
    try {
      await DeactivateProcess(dni.trim(), {
        code: code.trim(),
        reason,
        comment: comment.trim(),
      });
      const session = sessionGet();
      if (session) {
        await Logout();
      }
      setStep(STEP_DONE);
    } catch (e) {
      showToast(
        toastDispatch,
        mapBackendError(
          e,
          "No pudimos procesar la baja. Por favor, intentalo más tarde.",
        ),
        ToastType.ERROR,
        ToastType.ERROR,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderDniStep = () => (
    <form onSubmit={handleRequestCode} noValidate>
      <p
        style={{ color: "#555", fontSize: "0.95rem", marginBottom: "1.25rem" }}
      >
        Ingresá tu DNI para iniciar la baja. Te enviaremos un código de
        confirmación por WhatsApp y email.
      </p>
      <TextField
        label="DNI*"
        value={dni}
        onChange={(e) => {
          setDni(e.target.value);
          if (errors.dni) setErrors({});
        }}
        error={Boolean(errors.dni)}
        helperText={errors.dni || " "}
        fullWidth
        disabled={submitting}
        inputProps={{ inputMode: "numeric", maxLength: 10 }}
      />
      <div
        style={{
          marginTop: "1.5rem",
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={submitting}
          style={{ minWidth: "9rem", borderRadius: "25px" }}
        >
          Volver
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={submitting}
          style={{
            minWidth: "9rem",
            borderRadius: "25px",
            backgroundColor: submitting ? "#999" : DANGER,
            color: "white",
          }}
        >
          {submitting ? "Enviando..." : "Solicitar código de baja"}
        </Button>
      </div>
    </form>
  );

  const renderCodeStep = () => (
    <form onSubmit={handleProcess} noValidate>
      <p
        style={{ color: "#555", fontSize: "0.95rem", marginBottom: "1.25rem" }}
      >
        Te enviamos un código de confirmación
        {maskedMail ? (
          <>
            {" "}
            a <strong>{maskedMail}</strong> y por WhatsApp
          </>
        ) : (
          " por WhatsApp y email"
        )}
        . El código vence a las 24 hs.
      </p>

      <TextField
        label="Código de confirmación*"
        value={code}
        onChange={(e) => {
          setCode(e.target.value);
          if (errors.code) setErrors({ ...errors, code: undefined });
        }}
        error={Boolean(errors.code)}
        helperText={errors.code || " "}
        fullWidth
        disabled={submitting}
        inputProps={{ inputMode: "numeric", maxLength: 8 }}
      />

      <TextField
        select
        label="Motivo de la baja*"
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
          if (errors.reason) setErrors({ ...errors, reason: undefined });
        }}
        error={Boolean(errors.reason)}
        helperText={errors.reason || " "}
        fullWidth
        disabled={submitting || loadingReasons}
      >
        {loadingReasons && (
          <MenuItem value="" disabled>
            Cargando motivos...
          </MenuItem>
        )}
        {!loadingReasons && reasons.length === 0 && (
          <MenuItem value="" disabled>
            No hay motivos disponibles
          </MenuItem>
        )}
        {reasons.map((r) => (
          <MenuItem key={r.id || r.reason} value={r.reason}>
            {r.label || r.reason}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Comentario (opcional)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        fullWidth
        multiline
        minRows={3}
        maxRows={6}
        disabled={submitting}
        inputProps={{ maxLength: 500 }}
        helperText=" "
      />

      <div style={{ marginTop: "0.25rem" }}>
        <Button
          variant="text"
          onClick={handleResendCode}
          disabled={submitting}
          style={{ color: PRIMARY, textTransform: "none", paddingLeft: 0 }}
        >
          Reenviar código
        </Button>
      </div>

      <div
        style={{
          marginTop: "1.5rem",
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            setStep(STEP_DNI);
            setCode("");
            setReason("");
            setComment("");
            setErrors({});
          }}
          disabled={submitting}
          style={{ minWidth: "9rem", borderRadius: "25px" }}
        >
          Volver
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={submitting}
          style={{
            minWidth: "9rem",
            borderRadius: "25px",
            backgroundColor: submitting ? "#999" : DANGER,
            color: "white",
          }}
        >
          {submitting ? "Procesando..." : "Confirmar baja"}
        </Button>
      </div>
    </form>
  );

  const renderDoneStep = () => (
    <div style={{ textAlign: "center" }}>
      <p style={{ color: "#2e7d32", fontSize: "1.1rem", fontWeight: 600 }}>
        Tu baja se procesó correctamente.
      </p>
      <p style={{ color: "#555", fontSize: "0.95rem" }}>
        Lamentamos que te vayas. Si fue un error, podés volver a registrarte
        cuando quieras.
      </p>
      <Button
        variant="contained"
        onClick={() => history.push("/")}
        style={{
          marginTop: "1rem",
          minWidth: "9rem",
          borderRadius: "25px",
          backgroundColor: PRIMARY,
          color: "white",
        }}
      >
        Volver al inicio
      </Button>
    </div>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem 1rem",
        backgroundColor: "#f5f6fa",
      }}
    >
      <Paper
        elevation={3}
        style={{
          width: "100%",
          maxWidth: "560px",
          padding: "2rem",
          borderRadius: "12px",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
          <img
            src={Logo}
            alt="Mediline"
            style={{ maxWidth: "180px", height: "auto" }}
          />
          <h1
            style={{
              color: PRIMARY,
              fontWeight: 600,
              fontSize: "1.75rem",
              marginTop: "1rem",
              marginBottom: "0.5rem",
            }}
          >
            Dar de baja mi plan
          </h1>
        </div>

        {step === STEP_DNI && renderDniStep()}
        {step === STEP_CODE && renderCodeStep()}
        {step === STEP_DONE && renderDoneStep()}
      </Paper>
    </div>
  );
};

export default DarDeBaja;
